import CheckIcon from "@mui/icons-material/Check";
import { Box, Button, Card, CardContent, Chip, FormControlLabel, Grid, List, ListItem, ListItemIcon, ListItemText, Stack, Switch, Typography } from "@mui/material";
import { useState } from "react";

import type { Plan } from "../billing_billing_portal/useData";
import { formatMoney } from "../i18n/format";

/** Plan comparison used on the public pricing page and in the in-app upgrade flow (CF-0062). */
export function PlanPicker({ plans, currentCode, onChoose, ctaLabel = "Choose", busy }: {
  plans: Plan[]; currentCode?: string | null; onChoose: (plan: Plan, interval: "month" | "year", addons: string[]) => void; ctaLabel?: string; busy?: boolean;
}) {
  const [annual, setAnnual] = useState(false);
  const [addons, setAddons] = useState<string[]>([]);
  const interval = annual ? "year" : "month";
  const base = plans.filter((p) => !p.is_addon);
  const extras = plans.filter((p) => p.is_addon);
  return (
    <Box>
      <Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
        <FormControlLabel control={<Switch checked={annual} onChange={(e) => setAnnual(e.target.checked)} />} label={<>Bill annually <Chip label="2 months free" size="small" color="secondary" sx={{ ml: 1 }} /></>} />
      </Stack>
      <Grid container spacing={2}>
        {base.map((p) => {
          const price = annual ? Number(p.annual_price) / 12 : Number(p.monthly_price);
          const isCurrent = p.code === currentCode;
          return (
            <Grid key={p.code} size={{ xs: 12, md: 12 / Math.max(1, base.length) }}>
              <Card sx={{ height: "100%", borderColor: p.tier === 2 ? "primary.main" : undefined, borderWidth: p.tier === 2 ? 2 : 1 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5">{p.name}</Typography>
                    {p.tier === 2 && <Chip label="Most popular" color="primary" size="small" />}
                    {isCurrent && <Chip label="Current plan" size="small" />}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>{p.description}</Typography>
                  <Typography variant="h3" sx={{ mt: 1 }}>{formatMoney(price, p.currency, 0)}<Typography component="span" variant="body2" color="text.secondary"> /month</Typography></Typography>
                  <Typography variant="caption" color="text.secondary">{p.included_users} users · {p.included_sites} site{p.included_sites > 1 ? "s" : ""} · extra seats {formatMoney(p.per_seat_monthly_price, p.currency, 0)}/user/month</Typography>
                  <List dense>
                    {p.features.map((f) => <ListItem key={f} disableGutters><ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon><ListItemText primary={f} /></ListItem>)}
                  </List>
                  <Button fullWidth variant={p.tier === 2 ? "contained" : "outlined"} disabled={busy || isCurrent} onClick={() => onChoose(p, interval, addons)}>
                    {isCurrent ? "Current plan" : ctaLabel}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      {extras.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Add-on modules</Typography>
          <Grid container spacing={1}>
            {extras.map((a) => (
              <Grid key={a.code} size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined" sx={{ cursor: "pointer", borderColor: addons.includes(a.code) ? "primary.main" : undefined }}
                  onClick={() => setAddons((s) => s.includes(a.code) ? s.filter((x) => x !== a.code) : [...s, a.code])}>
                  <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="body2" fontWeight={600}>{a.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{a.description}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{formatMoney(annual ? Number(a.annual_price) / 12 : a.monthly_price, a.currency, 0)}/month</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
}
